import { useEffect } from "react";
import { motion } from "framer-motion";
import { Instagram } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

const FEEDSPRING_SRC = "https://scripts.feedspring.com/instagram-attrs.js";

export default function InstagramFeed() {
  // Load the FeedSpring script AFTER this component has mounted so its
  // one-shot DOM scan sees the feedspring="post" template that React just
  // rendered. If the script is already on the page (SPA re-mount, hot
  // reload, or repeat visit), remove and re-inject it so it re-scans.
  useEffect(() => {
    const existing = document.querySelector(`script[src="${FEEDSPRING_SRC}"]`);
    if (existing) existing.remove();

    const s = document.createElement("script");
    s.src = FEEDSPRING_SRC;
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
  }, []);

  return (
    <section
      className="relative w-full px-6 md:px-16 py-16 md:py-24"
      style={{ background: "var(--cream)" }}
      data-testid="instagram-section"
      feedspring="inst_ewhlP8bBWnFGFV1HvfmdO"
      feed-options="render:dynamic|limit:8"
    >
      <div className="mx-auto max-w-7xl">
        <div className="frame flex items-center justify-between px-5 py-3">
          <span
            className="text-[0.58rem] track-sm uppercase"
            style={{ color: "var(--paper-dim)" }}
          >
            03 / Follow Along
          </span>
          <span
            className="text-[0.58rem] track-sm uppercase"
            style={{ color: "var(--paper-faint)" }}
          >
            @evoveskincare on Instagram
          </span>
        </div>

        {/* Header — avatar, handle and follow CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease }}
          className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
        >
          <div className="flex items-center gap-4">
            <div
              className="relative shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden"
              style={{ border: "1px solid var(--line)" }}
            >
              <img
                src="/evove-ig-dp.jpg"
                alt="evove Instagram avatar"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="instagram-avatar-fallback"
              />
              {/* FeedSpring will overlay the live avatar on top if it resolves */}
              <img
                feed-field="avatar"
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
            <div>
              <h2 className="headline text-2xl sm:text-3xl lg:text-4xl">
                Follow{" "}
                <span feed-field="username" data-testid="instagram-handle">
                  @evoveskincare
                </span>
              </h2>
              <p
                className="mt-1 text-[0.82rem] md:text-[0.9rem]"
                style={{ color: "var(--paper-dim)" }}
              >
                Behind the science, the ritual and the launch, live from the
                evove lab.
              </p>
            </div>
          </div>

          <a
            href="https://www.instagram.com/evoveskincare/"
            target="_blank"
            rel="noopener noreferrer"
            className="cta self-start md:self-auto inline-flex items-center gap-2"
            data-testid="instagram-follow-cta"
          >
            <Instagram size={14} strokeWidth={1.8} />
            Follow
          </a>
        </motion.div>

        {/*
          Post grid: FeedSpring clones the single <article feedspring="post">
          template up to `limit:8` times and injects the live Instagram data
          into any element carrying feed-field="…".
        */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease }}
          className="mt-10 md:mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5"
          data-testid="instagram-grid"
        >
          <article
            feedspring="post"
            data-testid="instagram-post-template"
            className="group relative block aspect-square overflow-hidden rounded-sm frame"
            style={{
              background:
                "linear-gradient(135deg, rgba(27,23,53,0.06), rgba(27,23,53,0.02))",
            }}
          >
            <a
              feed-field="link"
              target="_blank"
              rel="noopener noreferrer"
              className="absolute inset-0"
            >
              <img
                feed-field="img"
                alt="Instagram post"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
              />
              <span
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center"
                style={{ background: "rgba(20,15,44,0.35)" }}
              >
                <Instagram
                  size={22}
                  strokeWidth={1.4}
                  style={{ color: "var(--cream)" }}
                />
              </span>
            </a>
          </article>
        </motion.div>
      </div>
    </section>
  );
}
